// src/pages/Properties.jsx
import React, { useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import { properties, developers, locations, budgetRanges, handoverYears } from "@/data/mock";
import PropertyCard from "@/components/PropertyCard";
import { SlidersHorizontal, X } from "lucide-react";

const Properties = () => {
  const { t, lang } = useApp();
  const [filters, setFilters] = useState({
    developer: "all",
    location: "all",
    budget: "all",
    handover: "all",
  });
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    return properties.filter((p) => {
      if (filters.developer !== "all" && p.developer !== filters.developer) return false;
      if (filters.location !== "all" && p.location !== filters.location) return false;
      if (filters.handover !== "all" && p.handover !== Number(filters.handover)) return false;
      if (filters.budget !== "all") {
        const range = budgetRanges.find((b) => b.id === filters.budget);
        if (range && (p.startingPrice < range.min || p.startingPrice > range.max)) return false;
      }
      return true;
    });
  }, [filters]);

  const reset = () =>
    setFilters({ developer: "all", location: "all", budget: "all", handover: "all" });

  const Filter = ({ label, value, onChange, options, testid }) => (
    <div data-testid={`filter-${testid}`}>
      <p className="text-[10px] tracking-[0.22em] uppercase text-[#A0A5B5] mb-3">{label}</p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`filter-select-${testid}`}
        className="lux-input cursor-pointer appearance-none pe-6 bg-[#0A0E1A]"
        style={{ 
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%23C9A84C' fill='none' stroke-width='1.2'/></svg>")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 0 center"
        }}
      >
        <option value="all" className="bg-[#0A0E1A]">{t("filter_all")}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-[#0A0E1A]">
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <main data-testid="properties-page" className="bg-[#0A0E1A] pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mb-12">
          <p className="overline">{t("nav_properties")}</p>
          <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[1.05] text-[#F5F0E8]">
            Off-Plan Portfolio
          </h1>
          <p className="mt-5 text-[#A0A5B5] font-light max-w-xl leading-relaxed">
            Hand-selected launches across Dubai and Abu Dhabi — every project independently vetted.
          </p>
        </div>

        {/* Mobile filter toggle */}
        <button
          data-testid="mobile-filter-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden btn-ghost-gold inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase mb-6"
        >
          <SlidersHorizontal className="w-4 h-4" /> Filters
        </button>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Filters */}
          <aside
            data-testid="filters-panel"
            className={`lg:col-span-3 lg:block ${open ? "block" : "hidden"}`}
          >
            <div className="sticky top-28 border border-[#C9A84C]/15 p-7 bg-[#1E2235]/30 space-y-7">
              <div className="flex items-center justify-between">
                <p className="font-display text-xl text-[#F5F0E8]">Refine</p>
                <button
                  onClick={reset}
                  data-testid="reset-filters-btn"
                  className="text-[10px] tracking-[0.2em] uppercase text-[#C9A84C] hover:text-[#F5F0E8] transition-colors"
                >
                  {t("filter_reset")}
                </button>
              </div>
              <div className="gold-divider" />
              <Filter
                testid="developer"
                label={t("filter_developer")}
                value={filters.developer}
                onChange={(v) => setFilters({ ...filters, developer: v })}
                options={developers.map((d) => ({ value: d.id, label: d.name }))}
              />
              <Filter
                testid="location"
                label={t("filter_location")}
                value={filters.location}
                onChange={(v) => setFilters({ ...filters, location: v })}
                options={locations.map((l) => ({
                  value: l.id,
                  label: lang === "ar" ? l.name_ar : l.name_en,
                }))}
              />
              <Filter
                testid="budget"
                label={t("filter_budget")}
                value={filters.budget}
                onChange={(v) => setFilters({ ...filters, budget: v })}
                options={budgetRanges.map((b) => ({
                  value: b.id,
                  label: lang === "ar" ? b.label_ar : b.label_en,
                }))}
              />
              <Filter
                testid="handover"
                label={t("filter_handover")}
                value={filters.handover}
                onChange={(v) => setFilters({ ...filters, handover: v })}
                options={handoverYears.map((y) => ({ value: y, label: String(y) }))}
              />
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <p
              data-testid="results-count"
              className="text-xs tracking-[0.22em] uppercase text-[#A0A5B5] mb-6"
            >
              {filtered.length} {t("results_count")}
            </p>
            {filtered.length === 0 ? (
              <div className="border border-[#C9A84C]/15 p-16 text-center bg-[#1E2235]/30">
                <X className="w-10 h-10 text-[#C9A84C] mx-auto" />
                <p className="font-display text-2xl mt-5 text-[#F5F0E8]">No matches found.</p>
                <p className="text-sm text-[#A0A5B5] mt-2">
                  Try widening your filters or reset them.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-8">
                {filtered.map((p, i) => (
                  <PropertyCard key={p.id} property={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Properties;